import React, { useState } from "react";
import { Row, Col, Checkbox } from "antd";
import { EcommerceFlow, ExclusiveFlow, SuiFrensFlow, emailValidationExp, numericValidationExp, digitValidationExp } from 'utils/helper';
import TextBoxComponent from "components/FormComponent/TextBoxComponent";
import { FormTypeShipping } from 'components/Ecommerce/ECTheme/ECAddressComponent/constant';

interface AddressFormComponentProps {
  formData: any;
  formType: "billing" | "shipping";
  isBothSame: any;
  onChange: any;
  initialValues: any;
  setInitialValues: any;
  errors: any;
  setErrors: any;
  flowName: any;
}

const AddressFormComponent: React.FC<AddressFormComponentProps> = ({
  formData,
  formType,
  isBothSame,
  onChange,
  initialValues,
  setInitialValues,
  errors,
  setErrors,
  flowName
}) => {
  const isShippingForm = formType === FormTypeShipping;

  const removeError = ({ target: { name } }: any) => {
    setErrors({ ...errors, [name]: "" });
  };

  const onNormalInput = (name: any, value: any) => {
    setInitialValues({ ...initialValues, [name]: value });
    setErrors({ ...errors, [name]: "" });
  }

  const onZipVerify = (name: any, value: any) => {
    if (!numericValidationExp.test(value)) {
      setErrors({ ...errors, [name]: "Zipcode should be in an integer", });
      return;
    } else {
      setInitialValues({ ...initialValues, [name]: value });
      if (value.length < 5) {
        setErrors({ ...errors, [name]: "Zipcode are invalid", });
        return;
      } else {
        setErrors({ ...errors, [name]: "", });
        return;
      }
    }
  }

  const onNumberVerify = (name: any, value: any) => {
    if (!numericValidationExp.test(value)) {
      setErrors({ ...errors, [name]: "Contact should be in an integer", });
      return;
    } else {
      if (value.length < 11) {
        setInitialValues({ ...initialValues, [name]: value });
      } else {
        setErrors({ ...errors, [name]: "Contact are invalid", });
        return;
      }
    }
  }

  const onEmailVerify = (name: any, value: any) => {
    if (digitValidationExp.test(value)) {
      setErrors({ ...errors, [name]: "Email should not start with numbers", });
      return;
    }
    setInitialValues({ ...initialValues, [name]: value });
    if (!emailValidationExp.test(value) && value) {
      setErrors({ ...errors, [name]: "Please enter a valid email properly", });
      return;
    } else {
      setErrors({ ...errors, [name]: "", });
      return;
    }
  }

  const onStringVerify = (name: any, value: any) => {
    if (digitValidationExp.test(value)) {
      setErrors({ ...errors, [name]: "Name should be in an string", });
      return;
    } else {
      setInitialValues({ ...initialValues, [name]: value });
    }
  }

  const handleInput = ({ target: { name, value } }: any) => {
    if (name == "email" || name == "semail") {
      onEmailVerify(name, value);
    } else if (name == "phone" || name == "sphone") {
      onNumberVerify(name, value);
    } else if (name == "zip_code" || name == "szip_code") {
      onZipVerify(name, value);
    } else if (name == "first_name" || name == "sfirst_name") {
      onStringVerify(name, value);
    } else {
      onNormalInput(name, value);
    }
  };

  const personalDetailsFields = [
    {
      name: isShippingForm ? "first_name" : "sfirst_name",
      label: "First Name",
    },
    {
      name: isShippingForm ? "last_name" : "slast_name",
      label: "Last Name",
    },
    {
      name: isShippingForm ? "phone" : "sphone",
      label: "Phone",
    },
    {
      name: isShippingForm ? "email" : "semail",
      label: "Email",
    },
  ];

  const addressDetailsFields = [
    {
      name: isShippingForm ? "address_one" : "saddress_one",
      label: "Address 1",
    },
    {
      name: isShippingForm ? "address_two" : "saddress_two",
      label: "Address 2",
    },
    {
      name: isShippingForm ? "city" : "scity",
      label: "City",
    },
    {
      name: isShippingForm ? "state" : "sstate",
      label: "State",
    },
    {
      name: isShippingForm ? "zip_code" : "szip_code",
      label: "Zip Code",
    },
    {
      name: isShippingForm ? "country" : "scountry",
      label: "Country",
    },
  ];

  const renderFields = (fields: any[]) => {
    const pairs = [];
    for (let i = 0; i < fields.length; i += 2) {
      pairs.push(fields.slice(i, i + 2));
    }

    return pairs.map((pair, index) => (
      <Row gutter={{ xs: 20, sm: 20, md: 20, lg: 20 }} key={index}>
        {pair.map(({ name, label }, index) => (
          <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} key={index}>
            <TextBoxComponent
              name={name}
              title={label}
              label={label}
              placeholder={`Enter ${label}`}
              value={initialValues && initialValues[name]}
              error={errors && errors[name]}
              onChange={handleInput}
              onFocus={removeError}
              mandatory
            />
          </Col>
        ))}
      </Row>
    ));
  };

  const renderTypeContent = (getTypeName: any) => {
    switch (getTypeName) {
      case ExclusiveFlow: return (
        <>
          <div className="formSmSection">
            <p className="eMdText">Personal Details</p>
            {renderFields(personalDetailsFields)}
          </div>
          <div className="formSmSection">
            <p className="eMdText">Address Details</p>
            {renderFields(addressDetailsFields)}
          </div>
          <div className="checkBox">
            {isShippingForm && (
              <Row>
                <Col span={24}>
                  <Checkbox onChange={onChange} checked={isBothSame}>
                    <p className="eSmText fontBold"> Make billing details same as shipping details</p>
                  </Checkbox>
                </Col>
              </Row>
            )}
          </div>
        </>
      )
      case SuiFrensFlow: return (
        <>
          <div className="formSmSection">
            <fieldset>
              <legend className="sSmText fontSemiBold">Personal Details</legend>
              {renderFields(personalDetailsFields)}
            </fieldset>
          </div>
          <div className="formSmSection">
            <fieldset>
              <legend className="sSmText fontSemiBold">Address Details</legend>
              {renderFields(addressDetailsFields)}
            </fieldset>
          </div>
          <div className="checkBox">
            {isShippingForm && (
              <Row>
                <Col span={24}>
                  <Checkbox onChange={onChange} checked={isBothSame}>
                    <p className="sSmText fontSemiBold"> Make billing details same as shipping details</p>
                  </Checkbox>
                </Col>
              </Row>
            )}
          </div>
        </>
      )
      case EcommerceFlow: return (
        <>
          <div className="formSmSection">
            {renderFields(personalDetailsFields)}
            {renderFields(addressDetailsFields)}
          </div>
          <div className="checkBox">
            {isShippingForm && (
              <Row>
                <Col span={24}>
                  <Checkbox onChange={onChange} checked={isBothSame}>
                    <p className="fontBold "> Make billing details same as shipping details</p>
                  </Checkbox>
                </Col>
              </Row>
            )}
          </div>
        </>
      )
    }
  }

  return (
    <> {renderTypeContent(flowName)} </>
  );
};

export default AddressFormComponent;